import { cn } from '@/shared/lib/cn';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'outline' | 'success' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  children: ReactNode;
}

// Todos os variants ganham `border` (1px) — visível em outline, transparente
// nos demais. Sem isso, altura visual divergia em flex containers com
// align-items:stretch quando outline ficava do lado de primary/success/etc
// (border 1px puxava stretch → primary "crescia" 2px só por vizinhança).
// Vide ChannelsPage vs ContatosPage (lição empírica 2026-05-23).
const variants: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary:
    'bg-brand-500 text-white shadow-xs border border-transparent hover:bg-brand-600 active:bg-brand-700 disabled:bg-brand-300',
  outline:
    'bg-n-0 text-n-700 border border-n-300 hover:bg-n-50 hover:border-n-400 disabled:opacity-50',
  success:
    'bg-success-500 text-white shadow-xs border border-transparent hover:bg-success-600 active:bg-success-700 disabled:opacity-50',
  ghost: 'bg-transparent text-n-700 border border-transparent hover:bg-n-100 active:bg-n-200 disabled:opacity-50',
  danger: 'bg-danger-500 text-white border border-transparent hover:bg-danger-600 disabled:opacity-50',
};

const sizes: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'h-8 px-3 text-[11px]',
  md: 'px-3.5 py-[7px] text-[11px]',
  lg: 'px-[22px] py-[11px] text-[13px]',
};

export function Button({
  variant = 'primary',
  size = 'md',
  className,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex items-center justify-center gap-[7px] rounded-md font-bold uppercase tracking-[0.05em] transition-[background,box-shadow,border-color] duration-150 whitespace-nowrap',
        '[&>svg]:h-[13px] [&>svg]:w-[13px]',
        'focus-visible:outline-2 focus-visible:outline-brand-500 focus-visible:outline-offset-2',
        'disabled:cursor-not-allowed',
        variants[variant],
        sizes[size],
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}
